import React, { useMemo } from "react";
import {
  Box,
  Paper,
  IconButton,
  useTheme,
  Typography,
  Divider,
} from "@mui/material";
import GenericScheduleTitle from "./generic/GenericScheduleTitle";
import { FlagTriangleRight, ArrowRight, Trash2 } from "lucide-react";
import { ComponentType, SidebarTabs } from "../../../../constants";
import { useSearchParams } from "react-router-dom";
import { useSidebar } from "../../../../contexts/CurriculumSidebarContext";
import { useDialog } from "../../../../contexts/CurriculumComponentDialogContext";
import { useCurriculumDispatch } from "../../../../redux/hooks";
import { removeOverride } from "../../../../redux/curriculum/curriculum-slice";
import MinimizedTooltip from "../../../tooltip/MinimizedTooltip";
import { getCommonTransition } from "../../../../utils/mui";

interface IOverridesComponentProps {
  overrides: {
    id: number;
    slug: string;
    weekNumber: number;
    dayNumber: number;
  }[];
  baseComponent: {
    id: number;
    slug: string;
  } | null;
  type: ComponentType;
}

const OverridesComponent = (props: IOverridesComponentProps) => {
  const { overrides: unsortedOverrides, baseComponent, type } = props;
  const dispatch = useCurriculumDispatch();
  const { openSidebar } = useSidebar();
  const { openDialog } = useDialog();
  const [searchParams, _] = useSearchParams();
  const componentSearchParam = searchParams.get("component");
  const isDialogEnv = !!componentSearchParam;
  const theme = useTheme();

  // Only needed when showing the override list
  const overrides = useMemo(() => {
    return [...unsortedOverrides].sort((a, b) => {
      if (a.weekNumber === b.weekNumber) {
        return a.dayNumber - b.dayNumber;
      }
      return a.weekNumber - b.weekNumber;
    });
  }, [unsortedOverrides]);

  const getOverrideSlug = (id: number) => {
    return overrides.find((override) => override.id === id)?.slug;
  };

  const handleOverrideClick = (id: number) => {
    if (isDialogEnv) {
      openDialog(getOverrideSlug(id));
    } else {
      openSidebar(getOverrideSlug(id), SidebarTabs.SCHEDULE);
    }
  };

  const handleDeleteClick = (id: number) => {
    dispatch(removeOverride({ overrideId: id }));
  };

  const handleEditClick = (id: number) => {
    openDialog(getOverrideSlug(id));
  };

  const handleBaseClick = () => {
    if (!baseComponent) return;
    if (isDialogEnv) {
      openDialog(baseComponent.slug);
    } else {
      openSidebar(baseComponent.slug, SidebarTabs.SCHEDULE);
    }
  };

  // When a base component is provided, show a single "Main component" item.
  if (baseComponent !== null) {
    return (
      <Box>
        <Box sx={{ display: "flex", flexDirection: "row", mb: 0.5 }}>
          <GenericScheduleTitle title="Main component" />
        </Box>
        <Paper
          onClick={handleBaseClick}
          elevation={0}
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            paddingBlock: 0.5,
            paddingLeft: 1.5,
            paddingRight: 0.5,
            borderRadius: 3,
            cursor: "pointer",
            backgroundColor: theme.palette.grey[100],
            gap: 0.75,
            border: 1,
            borderColor: theme.palette.grey[600],
            borderStyle: "dashed",
            ":hover": {
              backgroundColor: theme.palette.grey[200],
            },
            transition: getCommonTransition(theme, "background-color"),
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleBaseClick();
            }}
          >
            <ArrowRight
              size={20}
              strokeWidth={2.5}
              color={theme.palette.common.black}
            />
          </IconButton>
          <Divider orientation={"vertical"} flexItem />
          <Typography
            component="span"
            style={{ fontSize: 15, fontWeight: 500 }}
          >
            Go to main {type.toString().toLowerCase()}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // If no overrides exist, don't render anything.
  if (overrides.length === 0) return null;

  // Render list of overrides.
  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "row", mb: 0.5 }}>
        <GenericScheduleTitle title="Overrides" />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
        {overrides.map((override) => (
          <Paper
            key={override.id}
            onClick={() => handleOverrideClick(override.id)}
            elevation={0}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              paddingBlock: 0.5,
              paddingLeft: 1.5,
              paddingRight: 0.5,
              borderRadius: 3,
              cursor: "pointer",
              backgroundColor: theme.palette.grey[100],
              gap: 0.75,
              border: 1,
              borderColor: theme.palette.grey[600],
              borderStyle: "dashed",
              ":hover": {
                backgroundColor: theme.palette.grey[200],
              },
              transition: getCommonTransition(theme, "background-color"),
            }}
          >
            <MinimizedTooltip title="Customize this day">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(override.id);
                }}
              >
                <FlagTriangleRight
                  size={20}
                  strokeWidth={2.5}
                  style={{ marginBottom: 2 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(override.id);
                  }}
                  color={theme.palette.common.black}
                />
              </IconButton>
            </MinimizedTooltip>

            <Divider orientation={"vertical"} flexItem />
            <Typography
              component="span"
              style={{ fontSize: 15, fontWeight: 500 }}
            >
              Week {override.weekNumber}, Day {override.dayNumber}
            </Typography>
            <Box sx={{ display: "flex", ml: "auto" }}>
              <MinimizedTooltip title="Remove customization from this day">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(override.id);
                  }}
                >
                  <Trash2
                    size={18}
                    strokeWidth={2.5}
                    color={theme.palette.common.black}
                  />
                </IconButton>
              </MinimizedTooltip>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default OverridesComponent;
